import React, { useMemo, useRef, useState, useEffect } from "react";
import BuildingStatusTable from "@src/components/table/BuildingStatusTable";
import useGetFieldTableContents from "@src/hooks/map/useGetFieldTableContents";
import { css } from "@emotion/react";
import { transferToNative } from "@src/lib/WebAppBridge";

declare global {
    interface Window {
        transferToNative?: (value: string, key: string) => void;
    }
}

interface BuildingInfo {
    floor_list: any[];
    regstrGbCdNm: string;
    totArea: { square: string; pyong: string };
}

const BuildingStatusTablePage = () => {
    const searchParams = new URLSearchParams(location.search);
    const pnu = searchParams.get("pnu") ?? "";
    const lat = searchParams.get("lat") ?? "";
    const lng = searchParams.get("lng") ?? "";

    const { data, isLoading, isError, error } = useGetFieldTableContents({ pnu });

    const tableContainerRef = useRef<HTMLDivElement>(null);
    const [isExpanded, setIsExpanded] = useState(false);

    const sendHeightToNative = () => {
        if (tableContainerRef.current) {
            const height = tableContainerRef.current.offsetHeight;

            transferToNative(String(height), "updateViewHeight");
            console.log(`Height sent to native: ${height}px`);
        }
    };

    useEffect(() => {
        const timer = setTimeout(() => {
            sendHeightToNative();
        }, 0);

        return () => clearTimeout(timer);
    }, [isLoading, isExpanded, data]);

    const handleExpandedChange = (newExpandedState: boolean) => {
        setIsExpanded(newExpandedState);
    };

    const tableData: BuildingInfo | undefined = useMemo(
        () => data?.payload?.result?.title_info_single?.[0] ?? data?.payload?.result?.title_info_total?.[0],
        [data],
    );

    const reversedBuildingInfo = useMemo(() => {
        if (!tableData || !Array.isArray(tableData.floor_list)) {
            return [];
        }

        return [...tableData.floor_list].sort((a, b) => {
            const floorA = a?.flrNoNm;
            const floorB = b?.flrNoNm;

            const isBasementA = floorA?.includes("B");
            const isBasementB = floorB?.includes("B");

            const floorNumA = parseInt(floorA?.replace("B", "").replace("층", "") ?? "");
            const floorNumB = parseInt(floorB?.replace("B", "").replace("층", "") ?? "");

            if (!isBasementA && isBasementB) {
                return -1;
            }
            if (isBasementA && !isBasementB) {
                return 1;
            }

            if (!isBasementA && !isBasementB) {
                return floorNumB - floorNumA;
            }

            if (isBasementA && isBasementB) {
                return floorNumA - floorNumB;
            }

            return 0;
        });
    }, [tableData]);

    const buildingDetails = useMemo(() => {
        if (!tableData) return null;

        const isShowTotalArea = tableData.regstrGbCdNm !== "집합";

        return {
            totArea: tableData.totArea,
            buildingInfo: reversedBuildingInfo,
            isShowTotalArea,
        };
    }, [tableData, reversedBuildingInfo]);

    if (isLoading) {
        return <div>로딩 중...</div>;
    }
    if (isError) {
        return <div>에러가 발생했습니다: {error instanceof Error ? error.message : "알 수 없는 에러"}</div>;
    }
    if (!buildingDetails) {
        return <div>건물 정보를 불러올 수 없습니다.</div>;
    }

    return (
        <div css={BuildingStatusTableLayout}>
            <div ref={tableContainerRef}>
                <BuildingStatusTable
                    buildingInfo={buildingDetails.buildingInfo}
                    totArea={buildingDetails.totArea}
                    isShowTotalArea={buildingDetails.isShowTotalArea}
                    onExpandedChange={handleExpandedChange}
                />
            </div>
        </div>
    );
};

const BuildingStatusTableLayout = css`
    padding: 14px;
    overflow-y: scroll;
`;

export default BuildingStatusTablePage;
